/**
 * Summary Cards Component
 * Displays key metrics for free tier users
 */

import React from 'react';
import { UserPlus, Users, Play, MessageSquare } from 'lucide-react';
import { SummaryStats } from '../types';

interface SummaryCardsProps {
  summary: SummaryStats;
}

const BRAND_PRIMARY = '#03e8d3';
const BRAND_SUCCESS = '#10B981';
const BRAND_WARNING = '#F59E0B';
const BRAND_PURPLE = '#8B5CF6';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, color }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <div style={{ color }}>{icon}</div>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
      <p className="text-3xl font-mono font-bold text-gray-900 mb-2">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
};

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        title="New Free Users"
        value={summary.new_users.toLocaleString()}
        subtitle={`of ${summary.total_free_users.toLocaleString()} total`}
        icon={<UserPlus className="w-6 h-6" />}
        color={BRAND_PRIMARY}
      />
      <MetricCard
        title="Active Free Users"
        value={summary.active_users.toLocaleString()}
        subtitle="with activity this period"
        icon={<Users className="w-6 h-6" />}
        color={BRAND_SUCCESS}
      />
      <MetricCard
        title="Total Simulations"
        value={summary.total_simulations.toLocaleString()}
        subtitle={`${summary.total_runs.toLocaleString()} total runs`}
        icon={<Play className="w-6 h-6" />}
        color={BRAND_WARNING}
      />
      <MetricCard
        title="Chat Sessions"
        value={summary.total_chats.toLocaleString()}
        subtitle="healthcare chat sessions"
        icon={<MessageSquare className="w-6 h-6" />}
        color={BRAND_PURPLE}
      />
    </div>
  );
};

export default SummaryCards;
